import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './GameDetailsOverlay.css';
import { getGameAccess } from '../../api/games';
import { formatText } from '../../utils/typografUtils';

export default function GameDetailsOverlay({ game, onClose = () => { }, onPlay = () => { } }) {
  const [showGameOptions, setShowGameOptions] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [access, setAccess] = useState('none');
  const [menuMode, setMenuMode] = useState('options'); // 'options' | 'success' | 'redirect'
  const [busy, setBusy] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const formatPrice = (priceCents, currency = 'RUB') => {
    if (!priceCents || priceCents <= 0) return 'Бесплатно';
    const symbol = currency === 'RUB' ? '₽' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
    const price = Math.round(priceCents / 100).toLocaleString('ru-RU');
    return `${price} ${symbol}`;
  };

  const handlePlayClick = () => {
    setShowGameOptions(true);
    setIsClosing(false);
  };

  // Функция шаринга
  const handleShare = useCallback(async () => {
    const shareUrl = `${window.location.origin}/game/share/${game.slug}`;
    const shareData = {
      title: game.title,
      text: game.subtitle || (game.description ? game.description.slice(0, 100) + '...' : ''),
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      }
    } catch (err) {
      // Пользователь отменил шаринг или произошла ошибка
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        // Fallback на копирование
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);
        } catch {
          console.error('Clipboard error');
        }
      }
    }
  }, [game]);

  const handleCloseOptions = useCallback(() => {
    if (!showGameOptions || isClosing || busy) return;
    setIsClosing(true);
    setBusy(false);
  }, [showGameOptions, isClosing, busy]);

  const handleMenuAnimationEnd = useCallback(() => {
    if (isClosing) {
      setShowGameOptions(false);
      setIsClosing(false);
      setMenuMode('options');
    }
  }, [isClosing]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const result = await getGameAccess(game.id);
        if (isMounted) setAccess(result?.access || 'none');
      } catch {
        if (isMounted) setAccess('none');
      }
    })();
    return () => { isMounted = false; };
  }, [game?.id]);

  // Подключаем web-component для .lottie при показе success/redirect
  useEffect(() => {
    if (menuMode !== 'success' && menuMode !== 'redirect') return;
    const alreadyLoaded = document.querySelector('script[data-dotlottie-player]');
    if (alreadyLoaded) return;
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@dotlottie/player-component@latest/dist/dotlottie-player.js';
    script.async = true;
    script.setAttribute('data-dotlottie-player', 'true');
    document.head.appendChild(script);
  }, [menuMode]);

  // Фокус + Esc для меню опций
  useEffect(() => {
    if (!showGameOptions) return;
    const firstBtn = menuRef.current?.querySelector('button');
    firstBtn?.focus?.();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleCloseOptions();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showGameOptions, handleCloseOptions]);

  const claimFree = useCallback(async () => {
    console.log('claimFree called for slug:', game?.slug);
    setBusy(true);
    try {
      const { claimFreeGameBySlug } = await import('../../api/games');
      const result = await claimFreeGameBySlug(game.slug);
      console.log('claimFreeGameBySlug result:', result);
      setAccess('full');
      setMenuMode('success');
    } catch (e) {
      console.error('claimFree error:', e);
      alert(e.message || 'Не удалось получить игру');
    } finally {
      setBusy(false);
    }
  }, [game?.slug]);

  const claimTrial = useCallback(async () => {
    setBusy(true);
    try {
      const { claimTrialBySlug } = await import('../../api/games');
      await claimTrialBySlug(game.slug);
      setAccess('trial');
      setMenuMode('success');
    } catch (e) {
      alert(e.message || 'Не удалось добавить пробную версию');
    } finally {
      setBusy(false);
    }
  }, [game?.slug]);

  const buyDigital = useCallback(async () => {
    console.log('buyDigital called', { priceCents: game.priceCents, slug: game.slug });
    if (!game.priceCents || game.priceCents <= 0) {
      console.log('Game is free, calling claimFree');
      await claimFree();
      return;
    }
    setBusy(true);
    setMenuMode('redirect');
    try {
      console.log('Calling createPayment with:', { gameId: game.id });
      const { createPayment } = await import('../../api/payments');

      // Получаем поля для формы от edge-функции
      const formData = await createPayment({
        gameId: game.id
      });

      console.log('Received form data:', formData);

      // Создаём HTML форму
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://pro.selfwork.ru/merchant/v1/init';

      // Добавляем скрытые поля
      const fields = {
        'order_id': formData.order_id,
        'amount': formData.amount,
        'signature': formData.signature
      };

      // Добавляем info поля (товары)
      if (formData.info && Array.isArray(formData.info)) {
        formData.info.forEach((item, index) => {
          fields[`info[${index}][name]`] = item.name;
          fields[`info[${index}][quantity]`] = item.quantity;
          fields[`info[${index}][amount]`] = item.amount;
        });
      }

      // Создаём input элементы
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      // Добавляем форму в DOM и отправляем
      document.body.appendChild(form);
      console.log('Submitting form to Selfwork');
      form.submit();

    } catch (e) {
      console.error('buyDigital error:', e);
      alert(e.message || 'Не удалось оформить получение игры');
      setMenuMode('options');
      setBusy(false);
    }
    // НЕ сбрасываем busy, так как произойдёт редирект
  }, [game, claimFree]);

  const goToMyGame = useCallback(() => {
    if (!game?.slug) return;
    navigate(`/my-games/${game.slug}`);
  }, [navigate, game?.slug]);

  // Автопереход в Мои игры через 3.5с при успешном добавлении
  useEffect(() => {
    if (menuMode !== 'success') return;
    const timerId = setTimeout(() => {
      window.location.href = '/my-games';
    }, 3500);
    return () => clearTimeout(timerId);
  }, [menuMode]);

  // Таймаут оплаты: через 6 сек закрываем меню
  useEffect(() => {
    if (menuMode !== 'redirect') return;

    const closeTimer = setTimeout(() => {
      setIsClosing(true);
      setBusy(false);
    }, 6000);

    return () => clearTimeout(closeTimer);
  }, [menuMode]);

  if (!game) return null;

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={`Описание игры ${game.title}`}>
      <div
        className="overlay-bg overlay-bg--current"
        aria-hidden="true"
        style={{ backgroundImage: `url(${game.coverUrl})` }}
      />
      <div className="overlay-content">
        <div className="overlay-body">
          <div className="overlay-scroll">
            <div className="game-hero-wrap">
              <img className="game-hero" src={game.coverUrl} alt={game.title} />
            </div>
            <h2 className="overlay-title">{formatText(game.title)}</h2>
            {game.subtitle && <div className="overlay-subtitle">{formatText(game.subtitle)}</div>}
            {game.description && (
              <p className="overlay-text">{formatText(game.description)}</p>
            )}
          </div>
        </div>
      </div>

      <div className="tabbar-wrap">
        <div className="tabbar single" onClick={onClose}>
          <img src="/icons/back-arrow.svg" alt="Закрыть" />
        </div>

        <div className="tabbar single" onClick={handleShare}>
          <img src="/icons/share.svg" alt="Поделиться" />
        </div>

        <button className="tab-btn-accent-large" aria-label="Получить" onClick={handlePlayClick} disabled={busy}>
          <span className="tab-btn-accent-large-icon">
            <img src="/icons/plus_white.svg" alt="" aria-hidden="true" />
          </span>
          Получить
        </button>
      </div>

      {showGameOptions && (
        <div className={`game-options-overlay ${isClosing ? 'closing' : ''}`} onClick={handleCloseOptions}>
          <motion.div
            layout
            className={`game-options-menu ${isClosing ? 'closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
            ref={menuRef}
            onAnimationEnd={handleMenuAnimationEnd}
          >
            <AnimatePresence mode="wait">
              {menuMode === 'options' ? (
                <motion.div key="options" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="menu-content">
                  <div className="game-options-title">Выберете вариант:</div>
                  {console.log('DEBUG green button:', { access, priceCents: game.priceCents, show: (access === 'none' || access === 'available_trial') && game.priceCents > 0 })}

                  {(access === 'none' || access === 'available_trial') && game.priceCents > 0 && (
                    <button
                      className="game-option-btn game-option-btn--trial"
                      onClick={claimTrial}
                      disabled={busy}
                    >
                      <img src="/icons/get-demo.svg" alt="Пробная версия" />
                      <div className="game-option-content">
                        <div className="game-option-text">Получить 7 карточек</div>
                        <div className="game-option-price">Бесплатно</div>
                      </div>
                    </button>
                  )}

                  {access === 'trial' && (
                    <button
                      className="game-option-btn game-option-btn--trial"
                      onClick={goToMyGame}
                      disabled={busy}
                    >
                      <img src="/icons/get-demo.svg" alt="Играть в пробную версию" />
                      <div className="game-option-content">
                        <div className="game-option-text">Играть в пробную версию</div>
                        <div className="game-option-price">Добавлено</div>
                      </div>
                    </button>
                  )}

                  <button
                    className="game-option-btn game-option-btn--digital"
                    onClick={access === 'full' ? goToMyGame : buyDigital}
                    disabled={busy}
                  >
                    <img src='/icons/buy-digital.svg' alt={access === 'full' ? 'Играть' : 'Цифровая игра'} />
                    <div className="game-option-content">
                      <div className="game-option-text">{access === 'full' ? 'Играть в игру' : (!game.priceCents || game.priceCents <= 0 ? 'Получить игру' : 'Купить онлайн версию')}</div>
                      <div className="game-option-price">{access === 'full' ? 'Добавлено' : <>{formatPrice(game.priceCents, game.currency)}{game.priceCents > 0 && <span className="game-option-old-price"> 790₽</span>}</>}</div>
                    </div>
                  </button>

                  {game.ozonLink && (
                    <button
                      className="game-option-btn game-option-btn--physical"
                      onClick={() => window.open(game.ozonLink, '_blank')}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={busy}
                    >
                      <img src="/icons/buy-ozon.svg" alt="Печатная игра" />
                      <div className="game-option-content">
                        <div className="game-option-text">Купить печатную версию</div>
                        <div className="game-option-price">Ссылка на OZON</div>
                      </div>
                    </button>
                  )}
                </motion.div>
              ) : menuMode === 'success' ? (
                <motion.div key="success" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="game-options-success menu-content">
                  <div className="success-anim">
                    <dotlottie-player src="/lotties/success-orange.lottie" background="transparent" speed="1" autoplay style={{ width: '150px', height: '150px', display: 'block' }} />
                  </div>
                  <div className="success-message">
                    Игра добавлена в вашу библиотеку
                  </div>
                </motion.div>
              ) : (
                <motion.div key="redirect" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="game-options-success menu-content">
                  <div className="success-anim">
                    <div className="payment-spinner"></div>
                  </div>
                  <div className="success-message">
                    Переходим на страницу оплаты
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      )}

      {/* Toast notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            className="share-toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            Ссылка скопирована
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}